import React, { useState, useEffect, useCallback } from 'react';
import { tareasAPI, clientesAPI } from '../services/api';
import './Tareas.css';

const TIPOS = ['tarea', 'recordatorio'];
const PRIORIDADES = ['baja', 'media', 'alta', 'urgente'];
const ESTADOS = ['pendiente', 'completada', 'cancelada'];

function Tareas() {
  const [tareas, setTareas] = useState([]);
  const [proximas, setProximas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tab, setTab] = useState('proximas'); // proximas | todas
  const [showForm, setShowForm] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'tarea',
    prioridad: 'media',
    cliente_id: '',
    fecha_limite: '',
    fecha_recordatorio: '',
  });

  const loadClientes = useCallback(async () => {
    try {
      const r = await clientesAPI.getAll(true);
      setClientes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setClientes([]);
    }
  }, []);

  const loadTareas = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtroEstado) filters.estado = filtroEstado;
      if (filtroTipo) filters.tipo = filtroTipo;
      if (filtroCliente) filters.cliente_id = filtroCliente;
      const r = await tareasAPI.getAll(filters);
      const data = r.data?.data ?? r.data ?? [];
      setTareas(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar tareas');
      setTareas([]);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroTipo, filtroCliente]);

  const loadProximas = useCallback(async () => {
    try {
      const r = await tareasAPI.getProximas(14);
      const data = r.data?.data ?? r.data ?? [];
      setProximas(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error al cargar próximas tareas:', e);
      setError(e.response?.data?.error || 'Error al cargar próximas tareas');
      setProximas([]);
    }
  }, []);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  useEffect(() => {
    loadTareas();
    loadProximas();
  }, [loadTareas, loadProximas]);

  const getClienteNombre = (t) => {
    if (!t.cliente_id) return null;
    const c = clientes.find((x) => x.id === t.cliente_id);
    return c ? `${c.nombre} ${c.apellidos}` : (t.cliente_nombre || '') + ' ' + (t.cliente_apellidos || '');
  };

  const filteredTareas = busqueda.trim()
    ? tareas.filter((t) => {
        const q = busqueda.toLowerCase();
        const tit = (t.titulo || '').toLowerCase();
        const desc = (t.descripcion || '').toLowerCase();
        const cli = (getClienteNombre(t) || '').toLowerCase();
        return tit.includes(q) || desc.includes(q) || cli.includes(q);
      })
    : tareas;

  const handleNueva = () => {
    setEditingTarea(null);
    setForm({
      titulo: '',
      descripcion: '',
      tipo: 'tarea',
      prioridad: 'media',
      cliente_id: '',
      fecha_limite: '',
      fecha_recordatorio: '',
    });
    setShowForm(true);
  };

  const handleEditar = (t) => {
    setEditingTarea(t);
    setForm({
      titulo: t.titulo || '',
      descripcion: t.descripcion || '',
      tipo: t.tipo || 'tarea',
      prioridad: t.prioridad || 'media',
      cliente_id: t.cliente_id ? String(t.cliente_id) : '',
      fecha_limite: t.fecha_limite || '',
      fecha_recordatorio: t.fecha_recordatorio ? t.fecha_recordatorio.slice(0, 16) : '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) {
      setError('El título es obligatorio');
      return;
    }
    setError(null);
    try {
      const payload = {
        ...form,
        cliente_id: form.cliente_id || null,
        fecha_limite: form.fecha_limite || null,
        fecha_recordatorio: form.fecha_recordatorio || null,
      };
      if (editingTarea) {
        await tareasAPI.update(editingTarea.id, payload);
        setSuccess('Tarea actualizada');
      } else {
        await tareasAPI.create(payload);
        setSuccess('Tarea creada');
      }
      setShowForm(false);
      setEditingTarea(null);
      loadTareas();
      loadProximas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    }
  };

  const handleCompletar = async (id, notas = null) => {
    try {
      await tareasAPI.complete(id, notas);
      setSuccess('Tarea completada');
      loadTareas();
      loadProximas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al completar');
    }
  };

  const handleReabrir = async (id) => {
    try {
      await tareasAPI.update(id, { estado: 'pendiente' });
      setSuccess('Tarea reabierta');
      loadTareas();
      loadProximas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al reabrir tarea');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await tareasAPI.delete(id);
      setSuccess('Tarea eliminada');
      loadTareas();
      loadProximas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleSincronizarCumpleanos = async () => {
    if (!window.confirm('¿Sincronizar cumpleaños de clientes? Se crearán recordatorios para los cumpleaños que no tengan tarea asociada.')) return;
    
    setError(null);
    try {
      // Cargar todos los clientes activos
      const clientesResponse = await clientesAPI.getAll(true);
      const todosClientes = Array.isArray(clientesResponse.data) ? clientesResponse.data : [];
      
      // Cargar todas las tareas existentes para verificar duplicados
      const tareasResponse = await tareasAPI.getAll({});
      const todasTareas = Array.isArray(tareasResponse.data?.data ?? tareasResponse.data) 
        ? (tareasResponse.data?.data ?? tareasResponse.data) 
        : [];
      
      let creadas = 0;
      const añoActual = new Date().getFullYear();
      
      // Procesar cada cliente con fecha de nacimiento
      for (const cliente of todosClientes) {
        if (!cliente.fecha_nacimiento) continue;
        
        // Parsear fecha de nacimiento
        const fechaNac = new Date(cliente.fecha_nacimiento);
        if (isNaN(fechaNac.getTime())) continue;
        
        // Calcular cumpleaños de este año o próximo año si ya pasó
        const mesCumple = fechaNac.getMonth();
        const diaCumple = fechaNac.getDate();
        let fechaCumple = new Date(añoActual, mesCumple, diaCumple);
        
        // Si el cumpleaños ya pasó este año, usar el próximo año
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        if (fechaCumple < hoy) {
          fechaCumple = new Date(añoActual + 1, mesCumple, diaCumple);
        }
        
        // Calcular fecha de recordatorio (2 días antes)
        const fechaRecordatorio = new Date(fechaCumple);
        fechaRecordatorio.setDate(fechaRecordatorio.getDate() - 2);
        
        // Verificar si ya existe una tarea para este cliente con este cumpleaños
        const tituloEsperado = `Cumpleaños de ${cliente.nombre} ${cliente.apellidos}`;
        const fechaLimiteStr = fechaCumple.toISOString().split('T')[0];
        const existeTarea = todasTareas.some(t => 
          t.cliente_id === cliente.id &&
          t.tipo === 'recordatorio' &&
          t.titulo === tituloEsperado &&
          t.fecha_limite === fechaLimiteStr
        );
        
        if (!existeTarea) {
          // Crear la tarea
          await tareasAPI.create({
            titulo: tituloEsperado,
            descripcion: `Recordatorio de cumpleaños de ${cliente.nombre} ${cliente.apellidos}`,
            tipo: 'recordatorio',
            prioridad: 'media',
            cliente_id: cliente.id,
            fecha_limite: fechaLimiteStr,
            fecha_recordatorio: fechaRecordatorio.toISOString().slice(0, 16),
            estado: 'pendiente'
          });
          creadas++;
        }
      }
      
      setSuccess(`Sincronización completada. Se ${creadas === 1 ? 'creó' : 'crearon'} ${creadas} ${creadas === 1 ? 'tarea' : 'tareas'} de cumpleaños.`);
      loadTareas();
      loadProximas();
      setTimeout(() => setSuccess(null), 5000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al sincronizar cumpleaños');
      console.error('Error sincronizando cumpleaños:', e);
    }
  };

  const renderLista = (lista, showCompletar = true) => (
    <ul className="tareas-lista">
      {lista.map((t) => (
        <li key={t.id} className={`tarea-item ${t.estado} prioridad-${t.prioridad}`}>
          <div className="tarea-main">
            <span className={`tipo-badge tipo-${t.tipo}`}>{t.tipo}</span>
            <span className={`prioridad-badge prioridad-${t.prioridad}`}>{t.prioridad}</span>
            <strong>{t.titulo}</strong>
            {getClienteNombre(t) && <span className="tarea-cliente"> · {getClienteNombre(t)}</span>}
          </div>
          {t.descripcion && <p className="tarea-desc">{t.descripcion}</p>}
          <div className="tarea-meta">
            {t.fecha_limite && <span>📅 Límite: {new Date(t.fecha_limite).toLocaleDateString('es-ES')}</span>}
            {t.fecha_recordatorio && <span>🔔 Recordatorio: {new Date(t.fecha_recordatorio).toLocaleString('es-ES')}</span>}
          </div>
          {t.estado === 'completada' && t.fecha_completada && (
            <p className="tarea-completada">Completada: {new Date(t.fecha_completada).toLocaleString('es-ES')}</p>
          )}
          <div className="tarea-actions">
            {t.estado === 'pendiente' && showCompletar && (
              <button type="button" className="btn btn-success btn-sm" onClick={() => handleCompletar(t.id)}>
                Completar
              </button>
            )}
            {t.estado === 'completada' && (
              <button type="button" className="btn btn-warning btn-sm" onClick={() => handleReabrir(t.id)}>
                Reabrir
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleEditar(t)}>Editar</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)}>Eliminar</button>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="tareas-page">
      <div className="page-header">
        <h2>Tareas y recordatorios</h2>
        <div className="tareas-actions">
          <button type="button" className="btn btn-secondary" onClick={handleSincronizarCumpleanos} title="Crear recordatorios de cumpleaños para todos los clientes">
            🎂 Sincronizar Cumpleaños
          </button>
          <button type="button" className="btn btn-primary" onClick={handleNueva}>
            Nueva tarea
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="tareas-tabs">
        <button type="button" className={tab === 'proximas' ? 'active' : ''} onClick={() => setTab('proximas')}>
          Próximas (14 días)
        </button>
        <button type="button" className={tab === 'todas' ? 'active' : ''} onClick={() => setTab('todas')}>
          Todas
        </button>
      </div>

      {tab === 'todas' && (
        <div className="tareas-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label>Estado</label>
              <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                <option value="">Todos</option>
                {ESTADOS.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Tipo</label>
              <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="">Todos</option>
                {TIPOS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Cliente</label>
              <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                ))}
              </select>
            </div>
            <div className="filter-group filter-search">
              <label>Buscar</label>
              <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Título, descripción, cliente..." />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Cargando...</p>
      ) : tab === 'proximas' ? (
        <div className="tareas-section">
          <h3>Próximas tareas y recordatorios</h3>
          {proximas.length === 0 ? (
            <p className="empty-msg">No hay tareas pendientes en los próximos 14 días.</p>
          ) : (
            renderLista(proximas)
          )}
        </div>
      ) : (
        <div className="tareas-section">
          <h3>Listado</h3>
          {filteredTareas.length === 0 ? (
            <p className="empty-msg">No hay tareas con los filtros aplicados.</p>
          ) : (
            renderLista(filteredTareas, filtroEstado !== 'completada')
          )}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content tarea-form" onClick={(e) => e.stopPropagation()}>
            <h3>{editingTarea ? 'Editar tarea' : 'Nueva tarea o recordatorio'}</h3>
            <div className="form-group">
              <label>Título *</label>
              <input type="text" value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Llamar a cliente X" />
            </div>
            <div className="form-group">
              <label>Descripción</label>
              <textarea value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} rows={3} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}>
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Prioridad</label>
                <select value={form.prioridad} onChange={(e) => setForm((f) => ({ ...f, prioridad: e.target.value }))}>
                  {PRIORIDADES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Cliente</label>
                <select value={form.cliente_id} onChange={(e) => setForm((f) => ({ ...f, cliente_id: e.target.value }))}>
                  <option value="">Ninguno</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fecha límite</label>
                <input type="date" value={form.fecha_limite} onChange={(e) => setForm((f) => ({ ...f, fecha_limite: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Fecha recordatorio</label>
                <input type="datetime-local" value={form.fecha_recordatorio} onChange={(e) => setForm((f) => ({ ...f, fecha_recordatorio: e.target.value }))} />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditingTarea(null); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tareas;
